/**
 * Top-anchored toast for storefront quote-cart additions. Modeled on
 * Shopify's cart-notification element (same `animate`/`active` class
 * pattern, same DOM shape) so it inherits the theme's existing CSS
 * for the slide-down behavior.
 *
 * Subscribes to the window-level `quote-cart:added` event fired by
 * quote-request.js; reads the running total via window.QuoteCart.
 */
if (!customElements.get('quote-notification')) {
  customElements.define(
    'quote-notification',
    class QuoteNotification extends HTMLElement {
      connectedCallback() {
        this.notification = this.querySelector('#quote-notification');
        this.imageEl = this.querySelector('[data-quote-notification-image]');
        this.titleEl = this.querySelector('[data-quote-notification-title]');
        this.optionsEl = this.querySelector('[data-quote-notification-options]');
        this.qtyEl = this.querySelector('[data-quote-notification-qty]');
        this.closeBtn = this.querySelector('[data-quote-notification-close]');
        this.continueBtn = this.querySelector('[data-quote-notification-continue]');
        this.header = document.querySelector('sticky-header');
        this.dismissTimer = null;
        this.onBodyClick = this.handleBodyClick.bind(this);

        this.notification?.addEventListener('keyup', (event) => {
          if (event.code === 'Escape') this.close();
        });
        this.closeBtn?.addEventListener('click', this.close.bind(this));
        this.continueBtn?.addEventListener('click', this.close.bind(this));
        window.addEventListener('quote-cart:added', this.handleAdded.bind(this));
      }

      handleAdded(event) {
        const item = event.detail?.item;
        if (!item) return;
        this.render(item);
        this.open();
      }

      render(item) {
        if (this.titleEl) this.titleEl.textContent = item.product_title || 'Item';

        // Variant options as `<dt>Name:</dt><dd>Value</dd>` rows, matching
        // the cart-notification's `.product-option` structure so theme CSS
        // (label vs value spacing, light text color) applies for free.
        if (this.optionsEl) {
          this.optionsEl.replaceChildren(
            ...(item.options || []).map(({ name, value }) => {
              const row = document.createElement('div');
              row.className = 'product-option';
              const dt = document.createElement('dt');
              dt.textContent = `${name}:`;
              const dd = document.createElement('dd');
              dd.textContent = value;
              row.append(dt, dd);
              return row;
            })
          );
        }

        if (this.qtyEl) {
          const total = window.QuoteCart?.count() ?? 0;
          this.qtyEl.textContent = `Qty: ${formatQty(item.quantity)} · ${total} in quote`;
        }

        if (this.imageEl) {
          if (item.image_url) {
            this.imageEl.src = item.image_url;
            this.imageEl.alt = item.product_title || '';
            this.imageEl.hidden = false;
          } else {
            this.imageEl.removeAttribute('src');
            this.imageEl.hidden = true;
          }
        }
      }

      open() {
        if (!this.notification) return;
        // Reveal the sticky header on scroll-collapsed pages so the toast
        // anchors correctly under it — mirrors cart-notification.js.
        if (this.header) this.header.reveal?.();

        this.notification.classList.add('animate', 'active');
        this.notification.addEventListener(
          'transitionend',
          () => {
            this.notification.focus({ preventScroll: true });
          },
          { once: true }
        );

        document.body.addEventListener('click', this.onBodyClick);
        this.scheduleDismiss();
      }

      close() {
        if (!this.notification) return;
        this.notification.classList.remove('active');
        document.body.removeEventListener('click', this.onBodyClick);
        clearTimeout(this.dismissTimer);
      }

      scheduleDismiss() {
        clearTimeout(this.dismissTimer);
        // 6s reads + reaches the buttons without lingering long enough to
        // feel like it's blocking the page during prolonged shopping.
        this.dismissTimer = setTimeout(() => this.close(), 6000);
      }

      handleBodyClick(event) {
        const target = event.target;
        if (target === this.notification || target.closest('quote-notification')) return;
        // Re-clicking the Add-to-quote button itself shouldn't dismiss the
        // toast — it's the reason it's about to be re-shown. Covers both the
        // default product button ([data-quote-add]) and the slc-configurator
        // quote button ([data-add-to-quote]); the opening click bubbles to
        // body here, so without this it would close the toast immediately.
        if (target.closest?.('[data-quote-add], [data-add-to-quote]')) return;
        this.close();
      }
    }
  );
}

function formatQty(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0';
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
}
